import { useEffect, useState } from 'react';
import { _isEmpty, _isNotEmpty } from '@/littledash';
import { type MetadataFieldWithValue } from '@/model/Metadata.model';
import { type StudyFormMetaDetails, type StudyFormSection, studyCreateSectionSlug } from '@/model/StudyForm.model';
import { type ID } from '@/model/Common.model';
import { BENCHLING_INTEGRATION_META_SLUG } from '@/helpers.constants.tsx';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError.ts';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';

export interface MetadataSection {
  name: string;
  metadata: Array<MetadataFieldWithValue>;
}

interface UseMetadataSectionsProps {
  studyMetadata: Array<MetadataFieldWithValue>;
  formTemplateId?: ID;
}

interface UseMetadataSectionsReturn {
  metadataSections: Array<MetadataSection>;
  isLoading: boolean;
  error?: Error;
  visibleMetadataCount: number;
}

// Helper function to filter metadata for overview display
const isMetadataVisibleInOverview = (item: MetadataFieldWithValue): boolean => {
  return !_isEmpty(item.value) && item.slug !== BENCHLING_INTEGRATION_META_SLUG;
};

// Helper function to check if section is singular Benchling integration
const isSingularBenchlingSection = (section: StudyFormSection): boolean => {
  return (
    section.metadata?.some(({ info }) => info?.slug === BENCHLING_INTEGRATION_META_SLUG) &&
    section.metadata?.length === 1
  );
};

// Helper function to get custom sections
const getCustomSections = (sections: Array<StudyFormSection>): Array<StudyFormSection> => {
  return sections
    .filter((section) => section.type === 'custom' && !isSingularBenchlingSection(section))
    .map((section) => ({
      ...section,
      metadataFields: [],
    }));
};

// Helper function to ensure default Metadata section exists
const ensureDefaultMetadataSection = (sections: Array<StudyFormSection>): void => {
  const hasMetadataSection = sections.some((section) => section.name === 'Metadata');

  if (!hasMetadataSection) {
    sections.push({
      name: 'Metadata',
      metadata: [],
      metadataFields: [],
      active: true,
      default_section_number: sections.length,
      type: 'custom',
      id: 0,
      created_at: '',
      slug: studyCreateSectionSlug.metadata,
    });
  }
};

// Helper function to process metadata for a single section
const processSectionMetadata = (
  section: StudyFormSection,
  studyMetadata: Array<MetadataFieldWithValue>
): Array<MetadataFieldWithValue> => {
  const sectionMetadata: Array<MetadataFieldWithValue> = [];

  section.metadata?.forEach((metadataInfo) => {
    const metaWithValue = studyMetadata.find((m) => m.glossary_id === metadataInfo.info.id);

    const metadataField: MetadataFieldWithValue = {
      ...metadataInfo.info,
      value: metaWithValue?.value ?? null,
      id: metaWithValue?.id || 0,
      created_at: metaWithValue?.created_at || '',
      updated_at: metaWithValue?.updated_at || '',
      glossary_id: metadataInfo.info.id,
      read_only: metaWithValue?.read_only ?? metadataInfo.info.read_only,
      required: metadataInfo.required,
      options: metaWithValue?.options ?? metadataInfo.info.options ?? [],
    };

    sectionMetadata.push(metadataField);
  });

  return sectionMetadata;
};

// Helper function to create metadata sections list
const createMetadataSectionsList = (
  sections: Array<StudyFormSection>,
  studyMetadata: Array<MetadataFieldWithValue>
): Array<MetadataSection> => {
  const sectionsList: Array<MetadataSection> = [];

  sections.forEach((section) => {
    const sectionMetadata = processSectionMetadata(section, studyMetadata);
    // Filter metadata to only show visible items for overview
    const filteredMetadata = sectionMetadata.filter(isMetadataVisibleInOverview);

    // Add section if it has visible metadata
    if (_isNotEmpty(filteredMetadata)) {
      sectionsList.push({
        name: section.name,
        metadata: filteredMetadata,
      });
    }
  });

  return sectionsList;
};

// Helper function to get all metadata IDs from sections
const getAllMetadataIdsFromSections = (sections: Array<StudyFormSection>): Array<ID> => {
  const idList: Array<ID> = [];

  sections.forEach((section) => {
    section.metadata?.forEach((meta) => {
      idList.push(meta.info.id);
    });
  });

  return idList;
};

// Helper function to add metadata that doesn't belong to any section
const addOrphanedMetadata = (
  sectionsList: Array<MetadataSection>,
  sections: Array<StudyFormSection>,
  studyMetadata: Array<MetadataFieldWithValue>
): void => {
  const metadataInSectionsIdList = getAllMetadataIdsFromSections(sections);
  const orphanedMetadata = studyMetadata.filter((meta) => !metadataInSectionsIdList.includes(meta.glossary_id));
  // Filter orphaned metadata to only show visible items for overview
  const filteredOrphanedMetadata = orphanedMetadata.filter(isMetadataVisibleInOverview);

  if (!_isEmpty(filteredOrphanedMetadata)) {
    sectionsList.push({
      name: 'Metadata',
      metadata: filteredOrphanedMetadata,
    });
  }
};

// Helper function to process form sections
const processFormSections = (
  formResponse: StudyFormMetaDetails,
  studyMetadata: Array<MetadataFieldWithValue>
): Array<MetadataSection> => {
  // Get custom sections from form
  const customSections = getCustomSections(formResponse.sections);

  // Ensure default "Metadata" section exists
  ensureDefaultMetadataSection(customSections);

  // Process each section to create metadata list
  const metadataSectionsList = createMetadataSectionsList(customSections, studyMetadata);

  // Add orphaned metadata to default section
  addOrphanedMetadata(metadataSectionsList, customSections, studyMetadata);

  return metadataSectionsList;
};

export const useMetadataSections = ({
  studyMetadata,
  formTemplateId,
}: UseMetadataSectionsProps): UseMetadataSectionsReturn => {
  const [metadataSections, setMetadataSections] = useState<Array<MetadataSection>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  // Calculate visible metadata count
  const visibleMetadataCount = studyMetadata.filter(isMetadataVisibleInOverview).length;

  useEffect(() => {
    const fetchFormTemplate = async () => {
      if (_isEmpty(formTemplateId)) {
        return;
      }

      setIsLoading(true);
      setError(undefined);

      try {
        const response = await Http.get(apiRoute('team.studies.forms.get', { formId: formTemplateId }));
        const formMetaResponse: StudyFormMetaDetails = response.data.data;
        const processedSections = processFormSections(formMetaResponse, studyMetadata);
        setMetadataSections(processedSections);
      } catch (err) {
        if (notAborted(err)) {
          const errorInstance = new InVivoError('Could not fetch study creation template.', {
            cause: err,
            slug: 'study-creation-template-load',
          });
          ExceptionHandler.captureException(errorInstance);
          setError(errorInstance);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchFormTemplate();
  }, [studyMetadata, formTemplateId]);

  return {
    metadataSections,
    isLoading,
    error,
    visibleMetadataCount,
  };
};

export { isMetadataVisibleInOverview };
